import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getApiBaseUrl } from '../../apiBase';

function adminApi() {
    return axios.create({
        baseURL: getApiBaseUrl(),
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
}

const STATUSES = ['queued', 'retrying', 'sent', 'failed', 'rejected'];
const STATUS_COLORS = {
    queued: '#b45309', retrying: '#b45309', sent: '#15803d', failed: '#b91c1c', rejected: '#7c3aed',
};
const PAGE_SIZE = 50;

const cell = { padding: '8px 10px', borderBottom: '1px solid #e2e8f0', fontSize: 13, verticalAlign: 'top' };
const labelStyle = { display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 };
const fieldStyle = { marginBottom: 14 };
const hintStyle = { fontSize: 12, color: '#94a3b8', margin: '4px 0 0' };

function saveBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
}

/** Журнал одного канала (Email или SMS): фильтры, таблица, выгрузка в CSV. */
function OutboundLog({ channel }) {
    const { t, i18n } = useTranslation(['adminOutbound']);
    const empty = { status: '', type: '', q: '', initiator: '', from: '', to: '' };
    const [draft, setDraft] = useState(empty);
    const [applied, setApplied] = useState(empty);
    const [page, setPage] = useState(1);
    const [data, setData] = useState({ items: [], total: 0 });
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [exporting, setExporting] = useState(false);

    const params = useMemo(() => {
        const p = { channel };
        Object.entries(applied).forEach(([k, v]) => { if (v) p[k] = v; });
        return p;
    }, [channel, applied]);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await adminApi().get('/admin/outbound/log', { params: { ...params, page, pageSize: PAGE_SIZE } });
            setData(res.data);
        } catch {
            setError(t('adminOutbound:log.loadError'));
        }
        setLoading(false);
    }, [params, page, t]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        adminApi().get('/admin/outbound/log/types', { params: { channel } })
            .then(res => setTypes(res.data ?? []))
            .catch(() => setTypes([]));
    }, [channel]);

    function apply(e) {
        e?.preventDefault();
        setPage(1);
        setApplied(draft);
    }

    function reset() {
        setDraft(empty);
        setApplied(empty);
        setPage(1);
    }

    async function exportCsv() {
        setExporting(true);
        try {
            const res = await adminApi().get('/admin/outbound/log/export.csv', { params, responseType: 'blob' });
            saveBlob(res.data, `outbound-log-${channel}-${new Date().toISOString().slice(0, 10)}.csv`);
        } catch {
            setError(t('adminOutbound:log.exportError'));
        }
        setExporting(false);
    }

    const fmt = (iso) => (iso ? new Date(iso).toLocaleString(i18n.language) : '');
    const typeLabel = (type) => t(`adminOutbound:types.${type}`, { defaultValue: type });
    const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

    return (
        <div className="sp-panel">
            <form onSubmit={apply} style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginBottom: 14 }}>
                <label style={{ fontSize: 12, color: '#64748b' }}>
                    {t('adminOutbound:log.status')}
                    <select className="sp-input" style={{ display: 'block' }} value={draft.status}
                        onChange={e => setDraft({ ...draft, status: e.target.value })}>
                        <option value="">{t('adminOutbound:log.allStatuses')}</option>
                        {STATUSES.map(s => <option key={s} value={s}>{t(`adminOutbound:statuses.${s}`)}</option>)}
                    </select>
                </label>
                <label style={{ fontSize: 12, color: '#64748b' }}>
                    {t('adminOutbound:log.type')}
                    <select className="sp-input" style={{ display: 'block' }} value={draft.type}
                        onChange={e => setDraft({ ...draft, type: e.target.value })}>
                        <option value="">{t('adminOutbound:log.allTypes')}</option>
                        {types.map(x => <option key={x} value={x}>{typeLabel(x)}</option>)}
                    </select>
                </label>
                <label style={{ fontSize: 12, color: '#64748b' }}>
                    {t('adminOutbound:log.recipient')}
                    <input className="sp-input" style={{ display: 'block' }} value={draft.q}
                        placeholder={t('adminOutbound:log.recipientPlaceholder')}
                        onChange={e => setDraft({ ...draft, q: e.target.value })} />
                </label>
                <label style={{ fontSize: 12, color: '#64748b' }}>
                    {t('adminOutbound:log.initiator')}
                    <input className="sp-input" style={{ display: 'block' }} value={draft.initiator}
                        placeholder={t('adminOutbound:log.initiatorPlaceholder')}
                        onChange={e => setDraft({ ...draft, initiator: e.target.value })} />
                </label>
                <label style={{ fontSize: 12, color: '#64748b' }}>
                    {t('adminOutbound:log.from')}
                    <input type="date" className="sp-input" style={{ display: 'block' }} value={draft.from}
                        onChange={e => setDraft({ ...draft, from: e.target.value })} />
                </label>
                <label style={{ fontSize: 12, color: '#64748b' }}>
                    {t('adminOutbound:log.to')}
                    <input type="date" className="sp-input" style={{ display: 'block' }} value={draft.to}
                        onChange={e => setDraft({ ...draft, to: e.target.value })} />
                </label>
                <button type="submit" className="sp-btn sp-btn--primary">{t('adminOutbound:log.apply')}</button>
                <button type="button" className="sp-btn sp-btn--ghost" onClick={reset}>{t('adminOutbound:log.reset')}</button>
                <button type="button" className="sp-btn sp-btn--ghost" disabled={exporting} onClick={exportCsv}>
                    {exporting ? t('adminOutbound:log.exporting') : t('adminOutbound:log.exportCsv')}
                </button>
            </form>

            {error && <p style={{ color: '#c0392b', fontSize: 13 }}>{error}</p>}
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 8px' }}>
                {t('adminOutbound:log.total', { count: data.total })}
            </p>

            {loading ? (
                <div className="sp-loading-page">{t('adminOutbound:common.loading')}</div>
            ) : data.items.length === 0 ? (
                <p style={{ fontSize: 13, color: '#64748b' }}>{t('adminOutbound:log.empty')}</p>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                        <thead>
                            <tr style={{ textAlign: 'left', fontSize: 12, color: '#64748b' }}>
                                <th style={cell}>{t('adminOutbound:log.cols.when')}</th>
                                <th style={cell}>{t('adminOutbound:log.cols.type')}</th>
                                <th style={cell}>{t('adminOutbound:log.cols.recipient')}</th>
                                <th style={cell}>{t('adminOutbound:log.cols.initiator')}</th>
                                <th style={cell}>{t('adminOutbound:log.cols.school')}</th>
                                <th style={cell}>{t('adminOutbound:log.cols.status')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map(row => (
                                <tr key={row.id}>
                                    <td style={{ ...cell, whiteSpace: 'nowrap' }}>{fmt(row.createdAt)}</td>
                                    <td style={cell}>
                                        <div style={{ fontWeight: 600 }}>{typeLabel(row.type)}</div>
                                        {row.subject && <div style={{ color: '#64748b' }}>{row.subject}</div>}
                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                            {row.priority === 1 ? t('adminOutbound:log.urgent') : ''}
                                            {row.priority === 1 ? ' · ' : ''}
                                            {row.source === 'queue' ? t('adminOutbound:log.viaQueue') : t('adminOutbound:log.direct')}
                                        </div>
                                    </td>
                                    <td style={cell}>
                                        <div>{row.recipientEmail || row.recipientPhone}</div>
                                        {row.recipientName && row.recipientName !== row.recipientEmail && (
                                            <div style={{ color: '#64748b' }}>{row.recipientName}</div>
                                        )}
                                    </td>
                                    <td style={cell}>{row.initiatedBy}</td>
                                    <td style={cell}>{row.schoolName}</td>
                                    <td style={cell}>
                                        <div style={{ color: STATUS_COLORS[row.status], fontWeight: 600 }}>
                                            {t(`adminOutbound:statuses.${row.status}`, { defaultValue: row.status })}
                                        </div>
                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                            {row.attempts > 0 && t('adminOutbound:log.attempts', { count: row.attempts })}
                                        </div>
                                        {row.sentAt && (
                                            <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                                {t('adminOutbound:log.sentAt', { value: fmt(row.sentAt) })}
                                            </div>
                                        )}
                                        {row.error && <div style={{ fontSize: 12, color: '#b91c1c', maxWidth: 260 }}>{row.error}</div>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {pages > 1 && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14 }}>
                    <button type="button" className="sp-btn sp-btn--ghost" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                        {t('adminOutbound:log.prev')}
                    </button>
                    <span style={{ fontSize: 13, color: '#64748b' }}>{t('adminOutbound:log.page', { page, pages })}</span>
                    <button type="button" className="sp-btn sp-btn--ghost" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                        {t('adminOutbound:log.next')}
                    </button>
                </div>
            )}
        </div>
    );
}

function NumberField({ label, hint, value, onChange, min = 0, max }) {
    return (
        <div style={fieldStyle}>
            <label style={labelStyle}>{label}</label>
            <input
                type="number" className="sp-input" min={min} max={max} value={value}
                onChange={e => onChange(Math.max(min, Number(e.target.value) || 0))}
                style={{ maxWidth: 200 }}
            />
            {hint && <p style={hintStyle}>{hint}</p>}
        </div>
    );
}

/** Карточка настроек одного канала + текущая загрузка. */
function ChannelLimits({ channel, limits, usage, queue, onChange }) {
    const { t } = useTranslation(['adminOutbound']);
    const set = (key, value) => onChange({ ...limits, [key]: value });
    const zero = t('adminOutbound:limits.zeroHint');

    return (
        <div className="sp-panel" style={{ marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 10px' }}>{t(`adminOutbound:limits.${channel}`)}</h3>

            <div style={{
                display: 'flex', flexWrap: 'wrap', gap: 16, padding: 12, background: '#f8fafc',
                borderRadius: 10, marginBottom: 16, fontSize: 13,
            }}>
                <div><b>{usage?.lastMinute ?? 0}</b> {t('adminOutbound:limits.usageMinute')}</div>
                <div><b>{usage?.lastHour ?? 0}</b> {t('adminOutbound:limits.usageHour')}</div>
                <div><b>{usage?.last24Hours ?? 0}</b> {t('adminOutbound:limits.usageDay')}</div>
                <div><b>{usage?.distinctRecipients24Hours ?? 0}</b> {t('adminOutbound:limits.usageDistinct')}</div>
                <div><b>{usage?.queued ?? 0}</b> {t('adminOutbound:limits.queued')}</div>
                <div><b>{usage?.failed24Hours ?? 0}</b> {t('adminOutbound:limits.failed')}</div>
                <div style={{ color: '#64748b' }}>
                    {t('adminOutbound:limits.queueTitle')}:{' '}
                    {t('adminOutbound:limits.queuePending')} {queue?.pending ?? 0},{' '}
                    {t('adminOutbound:limits.queueRetrying')} {queue?.retrying ?? 0},{' '}
                    {t('adminOutbound:limits.queueFailed')} {queue?.failed ?? 0}
                </div>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!limits.enabled} onChange={e => set('enabled', e.target.checked)} />
                {t('adminOutbound:limits.enabled')}
            </label>
            <p style={{ ...hintStyle, marginBottom: 16 }}>{t('adminOutbound:limits.enabledHint')}</p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '0 24px' }}>
                <NumberField label={t('adminOutbound:limits.perMinute')} hint={zero} value={limits.perMinute} onChange={v => set('perMinute', v)} />
                <NumberField label={t('adminOutbound:limits.perHour')} hint={zero} value={limits.perHour} onChange={v => set('perHour', v)} />
                <NumberField label={t('adminOutbound:limits.perDay')} hint={zero} value={limits.perDay} onChange={v => set('perDay', v)} />
                <NumberField
                    label={t('adminOutbound:limits.distinct')} hint={`${zero}. ${t('adminOutbound:limits.distinctHint')}`}
                    value={limits.distinctRecipientsPerDay} onChange={v => set('distinctRecipientsPerDay', v)}
                />
                <NumberField label={t('adminOutbound:limits.minInterval')} value={limits.minIntervalSeconds} max={3600}
                    onChange={v => set('minIntervalSeconds', v)} />
                <NumberField label={t('adminOutbound:limits.reserve')} hint={t('adminOutbound:limits.reserveHint')}
                    value={limits.priorityReservePercent} max={90} onChange={v => set('priorityReservePercent', v)} />
                <NumberField label={t('adminOutbound:limits.batch')} value={limits.batchSize} min={1} max={500}
                    onChange={v => set('batchSize', v)} />
                <NumberField label={t('adminOutbound:limits.maxAttempts')} value={limits.maxAttempts} min={1} max={10}
                    onChange={v => set('maxAttempts', v)} />
                <NumberField label={t('adminOutbound:limits.retryDelay')} value={limits.retryDelayMinutes} min={1} max={1440}
                    onChange={v => set('retryDelayMinutes', v)} />
            </div>
        </div>
    );
}

function LimitsTab() {
    const { t } = useTranslation(['adminOutbound']);
    const [overview, setOverview] = useState(null);
    const [config, setConfig] = useState(null);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState('');
    const [msgIsError, setMsgIsError] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await adminApi().get('/admin/outbound/overview');
            setOverview(res.data);
            setConfig(res.data.limits);
        } catch {
            setMsg(t('adminOutbound:limits.loadError'));
            setMsgIsError(true);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    async function save() {
        setSaving(true);
        setMsg('');
        try {
            const res = await adminApi().put('/admin/outbound/limits', config);
            setConfig(res.data);
            setMsg(t('adminOutbound:limits.saved'));
            setMsgIsError(false);
            load();
        } catch {
            setMsg(t('adminOutbound:limits.saveError'));
            setMsgIsError(true);
        }
        setSaving(false);
    }

    if (!config || !overview) {
        return msg
            ? <p style={{ color: '#c0392b', fontSize: 13 }}>{msg}</p>
            : <div className="sp-loading-page">{t('adminOutbound:common.loading')}</div>;
    }

    return (
        <>
            <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, marginBottom: 16 }}>{t('adminOutbound:limits.explain')}</p>

            {msg && (
                <p style={{
                    marginBottom: 12, fontSize: 13, padding: '10px 14px', borderRadius: 8,
                    background: msgIsError ? '#fdecea' : '#e8f5e9', color: msgIsError ? '#c0392b' : '#2e7d32',
                }}>
                    {msg}
                </p>
            )}

            <ChannelLimits channel="email" limits={config.email} usage={overview.usage.email} queue={overview.queue.email}
                onChange={v => setConfig({ ...config, email: v })} />
            <ChannelLimits channel="sms" limits={config.sms} usage={overview.usage.sms} queue={overview.queue.sms}
                onChange={v => setConfig({ ...config, sms: v })} />

            <div className="sp-panel" style={{ marginBottom: 16 }}>
                <NumberField label={t('adminOutbound:limits.logRetention')} hint={t('adminOutbound:limits.logRetentionHint')}
                    value={config.logRetentionDays} min={7} max={3650}
                    onChange={v => setConfig({ ...config, logRetentionDays: v })} />
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" className="sp-btn sp-btn--primary" disabled={saving} onClick={save}>
                    {saving ? t('adminOutbound:limits.saving') : t('adminOutbound:limits.save')}
                </button>
                <button type="button" className="sp-btn sp-btn--ghost" onClick={() => setConfig(overview.defaults)}>
                    {t('adminOutbound:limits.resetDefaults')}
                </button>
                <a href="/admin/notifications/queue" style={{ fontSize: 13, color: '#2563eb' }}>
                    {t('adminOutbound:limits.queuePage')}
                </a>
            </div>
        </>
    );
}

export default function OutboundMessagesPage() {
    const { t } = useTranslation(['adminOutbound']);
    const [tab, setTab] = useState('email');

    const tabs = [
        { key: 'email', label: t('adminOutbound:tabs.email') },
        { key: 'sms', label: t('adminOutbound:tabs.sms') },
        { key: 'limits', label: t('adminOutbound:tabs.limits') },
    ];

    return (
        <OwnerPageLayout title={t('adminOutbound:pageTitle')} maxWidth={1200}>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>{t('adminOutbound:subtitle')}</p>
            <p style={{ fontSize: 12, color: '#64748b', marginBottom: 16 }}>{t('adminOutbound:perServerNote')}</p>

            <div className="sp-tabs" style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                {tabs.map(item => (
                    <button
                        key={item.key}
                        type="button"
                        className={`sp-tab${tab === item.key ? ' sp-tab--active' : ''}`}
                        onClick={() => setTab(item.key)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            {tab === 'limits' ? <LimitsTab /> : <OutboundLog key={tab} channel={tab} />}
        </OwnerPageLayout>
    );
}
